import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Plus, FileWarning, ShieldAlert } from 'lucide-react';
import { cpdAPI, errMsg } from '../../lib/cpdRegisterAPI';
import { CPDFrameworkCard } from './CPDFrameworkCard';
import { CPDStatusBadge } from './CPDStatus';
import { CPDExceptions } from './CPDExceptions';
import { CPDMyTraining } from './CPDMyTraining';
import { CPDRecommendations } from './CPDAssistant';
import { toast } from 'sonner';

export const CPDDashboardTab = ({ userId, me, onAdd, refreshKey, title }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => cpdAPI.dashboard(userId).then(setD).catch((e) => toast.error(errMsg(e))), [userId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d) return <p className="text-sm text-muted-foreground p-6">Calculating…</p>;
  return (
    <div className="space-y-6" data-testid="cpd-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">{title || 'My CPD'}</h2>
          {d.overall_status && <CPDStatusBadge status={d.overall_status} testId="cpd-overall-status" />}
        </div>
        {onAdd && me.tier !== 'auditor' && <Button onClick={onAdd} data-testid="cpd-add-activity-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Add CPD activity</Button>}
      </div>
      {d.missing_evidence > 0 && (
        <Card className="border-amber-200 bg-amber-50/60"><CardContent className="py-3 flex items-center gap-2 text-sm text-amber-900" data-testid="cpd-missing-evidence-banner">
          <FileWarning className="w-4 h-4" /> {d.missing_evidence} verifiable activit{d.missing_evidence === 1 ? 'y has' : 'ies have'} no supporting evidence.
        </CardContent></Card>
      )}
      {d.frameworks.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="cpd-no-frameworks">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-slate-400" />No CPD frameworks are assigned yet. An administrator can assign them in Team → Registrations.
        </CardContent></Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">{d.frameworks.map((r) => <CPDFrameworkCard key={r.user_framework_id} r={r} />)}</div>
      )}
      <CPDRecommendations userId={d.user_id} refreshKey={refreshKey} />
      <CPDMyTraining me={me} userId={d.user_id === me.user_id ? undefined : d.user_id} refreshKey={refreshKey} />
      <CPDExceptions userId={d.user_id} me={me} items={d.exceptions} note={d.exception_note} onChanged={load} />
    </div>
  );
};
